import { routes } from 'lib/routes';
import { type ResponseBody } from 'lib/swagger_helper';

import { apiSlice } from 'api/base_slice';
import { POPULATION_ATTRIBUTES_TAG } from 'api/tag_types';
import {
  deserializeJSONApiResponse,
  headersAndCredentials,
  sortAlphabeticalByName,
} from 'api/utils';

export type PopulationAttributesRawResponse =
  ResponseBody<'getParticipantPopulationAttributes'>;
export type PopulationAttributeRawResponse =
  ResponseBody<'createParticipantPopulationAttribute'>;

export type PopulationAttributesAPIResponse = ReturnType<
  typeof sortAlphabeticalByName<ReturnType<typeof deserializeJSONApiResponse>>
>;

type DeletePopulationAttributeParams = {
  participantPopulationId: string;
  participantPopulationAttributeId: string;
};

type UpdatePopulationAttributeParams = {
  participantPopulationId: string;
  participantPopulationAttributeId: string;
  patch: string;
};

type CreatePopulationAttributeParams = {
  participantPopulationId: string;
  name: string;
  type: string;
};

type GetPopulationAttributesParams = {
  participantPopulationId: string;
  countryCode?: string;
  projectId?: string;
};

export const populationAttributesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPopulationAttributes: builder.query<
      PopulationAttributesAPIResponse,
      GetPopulationAttributesParams
    >({
      query: ({ participantPopulationId, countryCode, projectId }) =>
        routes.api_participant_population_attributes_path(
          participantPopulationId,
          {
            ...(countryCode ? { country_code: countryCode } : {}),
            ...(projectId ? { project_id: projectId } : {}),
          },
        ),
      providesTags: [POPULATION_ATTRIBUTES_TAG],
      transformResponse: (
        response: PopulationAttributesRawResponse,
      ): PopulationAttributesAPIResponse =>
        sortAlphabeticalByName(deserializeJSONApiResponse(response)),
      mockRoute: routes.api_participant_population_attributes_path(
        ':participantPopulationId',
      ),
    }),
    deletePopulationAttribute: builder.mutation<
      void,
      DeletePopulationAttributeParams
    >({
      query: ({
        participantPopulationId,
        participantPopulationAttributeId,
      }) => ({
        url: routes.api_participant_population_attribute_path(
          participantPopulationId,
          participantPopulationAttributeId,
        ),
        method: 'DELETE',
        ...headersAndCredentials(),
      }),
      invalidatesTags: [POPULATION_ATTRIBUTES_TAG],
    }),
    updatePopulationAttribute: builder.mutation<
      PopulationAttributesRawResponse,
      UpdatePopulationAttributeParams
    >({
      query: ({
        participantPopulationId,
        participantPopulationAttributeId,
        ...patch
      }) => ({
        url: routes.api_participant_population_attribute_path(
          participantPopulationId,
          participantPopulationAttributeId,
        ),
        method: 'PATCH',
        body: {
          data: {
            attributes: patch,
          },
        },
        ...headersAndCredentials(),
      }),
      invalidatesTags: [POPULATION_ATTRIBUTES_TAG],
    }),
    createPopulationAttribute: builder.mutation<
      PopulationAttributeRawResponse,
      CreatePopulationAttributeParams
    >({
      query: ({ participantPopulationId, name, type }) => {
        const params = {
          data: {
            attributes: {
              name,
              type,
            },
          },
        };

        return {
          url: routes.api_participant_population_attributes_path(
            participantPopulationId,
          ),
          method: 'POST',
          body: params,
          ...headersAndCredentials(),
        };
      },
      invalidatesTags: [POPULATION_ATTRIBUTES_TAG],
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  mockGetPopulationAttributes,
  useCreatePopulationAttributeMutation,
  useDeletePopulationAttributeMutation,
  useGetPopulationAttributesQuery,
  useUpdatePopulationAttributeMutation,
} = populationAttributesApi;
